/**
 * PendingBindingsTable — Sprint 5.2A.
 *
 * Aba "Provisionamento > Pendentes". Lista os bindings com status
 * 'pending_device' — vínculos criados via QR mas que ainda esperam
 * o primeiro heartbeat do device para o auto-bind acontecer.
 *
 * Colunas: QR · Cliente · Template · Criado · Status · Device
 *
 * Atualiza sozinha a cada 15s (polling leve) — quando o auto-bind
 * acontece no backend, o binding some da lista de pendentes e o
 * operador vê o número cair.
 */
import { useCallback, useEffect, useState } from "react";
import { listPendingBindings } from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";

const POLL_MS = 15000;

export default function PendingBindingsTable({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback((signal) => {
    listPendingBindings({ signal })
      .then((data) => {
        setRows(data.bindings || []);
        setCount(data.count || 0);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") {
          setErr(e?.message || String(e));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    // Polling leve — pega o momento em que o auto-bind remove o item
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load, refreshKey]);

  return (
    <section className="card-pad space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          {t("provisioning.pendingTab.title")}
        </h2>
        <span className="text-[10px] text-noc-muted/70">
          {t("provisioning.pendingTab.autoHint")}
        </span>
      </div>

      {/* S3.11 — explica o fluxo atual (aguardando 1º heartbeat / auto-bind). */}
      <div className="text-[11px] text-noc-muted bg-noc-bg/40 border border-noc-border/50 rounded p-2 leading-relaxed">
        Vínculos criados via QR que ainda aguardam o{" "}
        <b className="text-noc-text">1º heartbeat</b> do Sonoff. Quando o equipamento
        liga e envia o primeiro heartbeat, o <b className="text-noc-text">auto-bind</b>{" "}
        ocorre automaticamente — o dispositivo vira operacional e some desta lista.
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="text-xs text-noc-muted">{t("common.loading")}</div>
      )}

      {!loading && rows.length === 0 && !err && (
        <div className="py-8 text-center">
          <div className="text-2xl mb-1">✓</div>
          <div className="text-sm text-noc-muted">
            Nenhum dispositivo aguardando o primeiro heartbeat.
          </div>
          <div className="text-[11px] text-noc-muted/60 mt-1">
            Novos vínculos aparecem aqui assim que um QR é provisionado.
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-noc-muted border-b border-noc-border">
                <Th>{t("provisioning.pendingTab.colQr")}</Th>
                <Th>{t("provisioning.pendingTab.colCustomer")}</Th>
                <Th>{t("provisioning.pendingTab.colTemplate")}</Th>
                <Th>{t("provisioning.pendingTab.colCreated")}</Th>
                <Th>{t("provisioning.pendingTab.colStatus")}</Th>
                <Th>{t("provisioning.pendingTab.colDevice")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-noc-border/40 hover:bg-noc-bg/30"
                >
                  <Td mono>{b.qr_serial}</Td>
                  <Td>
                    <span className="font-mono text-noc-accent">
                      #{b.customer_id}
                    </span>
                  </Td>
                  <Td>
                    {b.template_name ? (
                      <span className="text-noc-accent">{b.template_name}</span>
                    ) : (
                      <span className="text-noc-muted/60 italic">—</span>
                    )}
                  </Td>
                  <Td>
                    <span title={b.created_at}>
                      {formatRelative(b.created_at)}
                    </span>
                  </Td>
                  <Td>
                    <PendingBadge status={b.status} />
                  </Td>
                  <Td mono>
                    {b.device_id || (
                      <span className="text-noc-muted/60 italic">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-noc-muted/70 mt-2">
            {t("provisioning.pendingTab.countSuffix", { n: count })}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * PendingBadge — Sprint 5.2A.
 * PENDENTE (amarelo) ou VINCULADO (verde).
 */
export function PendingBadge({ status }) {
  if (status === "bound") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border border-noc-ok/30 text-noc-ok bg-noc-ok/10">
        ● {t("provisioning.pendingTab.badgeBound")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border border-noc-warn/30 text-noc-warn bg-noc-warn/10">
      ⏳ {t("provisioning.pendingTab.badgePending")}
    </span>
  );
}

function Th({ children }) {
  return <th className="text-left font-medium py-1.5 px-2">{children}</th>;
}

function Td({ children, mono }) {
  return (
    <td className={`py-1.5 px-2 ${mono ? "font-mono" : ""}`}>{children}</td>
  );
}
